import { IndexedDBStore } from "matrix-js-sdk";
import type { MatrixClient } from "matrix-js-sdk";
import { SlidingSync } from "matrix-js-sdk/lib/sliding-sync";

export const MATRIX_SYNC_MODE = {
    LEGACY: "legacy",
    SLIDING: "sliding",
} as const;

export type MatrixSyncMode = typeof MATRIX_SYNC_MODE[keyof typeof MATRIX_SYNC_MODE];

const SYNC_MODE_STORAGE_PREFIX = "dfm_matrix_sync_mode";
const SYNC_STORE_PREFIX = "dfm_matrix_sync_store";
const ROOM_LIST_NAME = "portal_rooms";
const INITIAL_ROOM_LIST_END = 19;
const SLIDING_SYNC_TIMEOUT_MS = 30_000;

export const REQUIRED_ROOM_STATE: string[][] = [
    ["m.room.create", ""],
    ["m.room.name", ""],
    ["m.room.canonical_alias", ""],
    ["m.room.avatar", ""],
    ["m.room.encryption", ""],
    ["m.room.power_levels", ""],
    ["m.room.member", "$ME"],
    // Encryption must know about quiet and offline group participants, not
    // only members represented by senders in the current timeline.
    ["m.room.member", "*"],
    ["org.matrix.msc3401.call.member", "*"],
];

interface ResolveMatrixSyncModeOptions {
    isNewInstallation: boolean;
    storedMode: string | null;
}

export function resolveMatrixSyncMode(options: ResolveMatrixSyncModeOptions): MatrixSyncMode {
    if (options.storedMode === MATRIX_SYNC_MODE.LEGACY || options.storedMode === MATRIX_SYNC_MODE.SLIDING) {
        return options.storedMode;
    }
    return options.isNewInstallation ? MATRIX_SYNC_MODE.SLIDING : MATRIX_SYNC_MODE.LEGACY;
}

export function createMatrixSyncStore(userId: string, deviceId: string): IndexedDBStore {
    const dbName = `${SYNC_STORE_PREFIX}_${encodeURIComponent(userId)}_${encodeURIComponent(deviceId)}_v1`;
    return new IndexedDBStore({ indexedDB: window.indexedDB, dbName });
}

export function getStoredMatrixSyncMode(userId: string, deviceId: string): string | null {
    return window.localStorage.getItem(getSyncModeStorageKey(userId, deviceId));
}

export function storeMatrixSyncMode(userId: string, deviceId: string, mode: MatrixSyncMode): void {
    window.localStorage.setItem(getSyncModeStorageKey(userId, deviceId), mode);
}

export function createSlidingSync(client: MatrixClient, baseUrl: string): SlidingSync {
    const lists = new Map([
        [ROOM_LIST_NAME, {
            ranges: [[0, INITIAL_ROOM_LIST_END]],
            sort: ["by_recency"],
            filters: { not_room_types: ["m.space"] },
            required_state: REQUIRED_ROOM_STATE,
            timeline_limit: 1,
        }],
    ]);
    const roomSubscription = {
        required_state: REQUIRED_ROOM_STATE,
        timeline_limit: 50,
    };
    return new SlidingSync(baseUrl, lists, roomSubscription, client, SLIDING_SYNC_TIMEOUT_MS);
}

export function expandSlidingSyncRoomList(slidingSync: SlidingSync): void {
    const joinedCount = slidingSync.getListData(ROOM_LIST_NAME)?.joinedCount;
    if (typeof joinedCount === "number" && joinedCount > INITIAL_ROOM_LIST_END + 1) {
        slidingSync.setListRanges(ROOM_LIST_NAME, [[0, joinedCount - 1]]);
    }
}

function getSyncModeStorageKey(userId: string, deviceId: string): string {
    return `${SYNC_MODE_STORAGE_PREFIX}_${encodeURIComponent(userId)}_${encodeURIComponent(deviceId)}`;
}
